import type {
  GetExternalAccountTxsQuery,
  LedgerAccountTypes,
} from 'hooks/customerApi';
import assert from 'assert';
import { DetailPane } from 'components/detail-pane/DetailPane';
import { makeLinePath } from 'components/SideNav/useDashboardNavTree';
import React from 'react';
import { formatDateTime } from 'utils/date';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { Icon } from '@fragment/ui/src/components/Icon';
import { LineIcon } from '@fragment/ui/src/components/Icon/LineIcon/LineIcon';
import { OverflowTooltip } from '@fragment/ui/src/components/Tooltip/OverflowTooltip';

import type { TxNode } from './types';

export type TxLink = NonNullable<
  GetExternalAccountTxsQuery['externalAccount']
>['link'];

type Props = {
  link: TxLink;
  tx: TxNode;
  account:
    | {
        id: string;
        type: LedgerAccountTypes;
      }
    | undefined;
};

type LedgerLine = NonNullable<
  NonNullable<TxNode['ledgerLines']>['nodes']
>[number];

export const TxDetail = ({
  link,
  tx: { posted, description, amount, currency, ledgerLines, externalId },
  account,
}: Props): JSX.Element => {
  const workspaceId = useWorkspaceId();
  assert(currency);

  // tx should be reconciled to 0 or 1 lines, but to be safe we'll just grab the first line
  const [line] = ledgerLines.nodes.filter(
    (l) => l.accountId === account?.id
  ) as [] | [LedgerLine];
  const schemaKey = line?.ledger.schema?.key;

  return (
    <DetailPane.Section>
      <DetailPane.V2Item label="Transaction" textToCopy={description}>
        <Badge badge={<Icon type={line ? 'reconciledTx' : 'unreconciledTx'} />}>
          <OverflowTooltip
            maxW="max-w-full"
            baseComponent={description}
            overflowComponent={description}
          />
        </Badge>
      </DetailPane.V2Item>
      <DetailPane.V2Item label="External ID" textToCopy={externalId}>
        {externalId}
      </DetailPane.V2Item>
      <DetailPane.V2Item label="Posted" textToCopy={posted}>
        {posted ? formatDateTime(posted) : 'N/A'}
      </DetailPane.V2Item>
      <DetailPane.V2Item label="Amount">
        <Amount amount={amount} currency={currency} />
      </DetailPane.V2Item>
      {account && line && (
        <DetailPane.V2Item
          label="Line"
          linkHref={makeLinePath({
            workspaceId,
            schemaKey,
            ledgerId: line.ledgerId,
            ledgerEntryId: line.ledgerEntryId,
            lineId: line.id,
          })}
        >
          <Badge badge={<LineIcon type={account.type} />}>
            {line.description ?? (
              <span className="text-main-500">No Description</span>
            )}
          </Badge>
        </DetailPane.V2Item>
      )}
    </DetailPane.Section>
  );
};
